/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {BUILD_VERSION, METRICS_VERSION} from './BuildVersion';
import Configuration from './Configuration';
import Metrics, {AdobeMetrics, Application, DigitalData, EventData, Level, Metric, MetricsEvents,
  MetricsState, PageData, Record, RecordType, Timer, User, UserData} from '@exc/metrics';
import PageState from './PageState';
import Payload from './Payload';
import Runtime from './Runtime';
import Transport from './Transport';
import uuidv1 from 'uuid/v1';

const re = /adobe_optout=/gi;
const PROD_FLUSH_PERIOD = 15000;
const ANONYMOUS_CONSTANT = 'anonymous';

const CONSOLE_NORMAL = {
  debug: false,
  error: true,
  info: false,
  metric: false,
  trace: false,
  warn: true
};
const CONSOLE_VERBOSE = {
  debug: true,
  error: true,
  info: true,
  metric: false,
  trace: true,
  warn: true
};
const CONSOLE_VVV = {
  debug: true,
  error: true,
  info: true,
  metric: true,
  trace: true,
  warn: true
};
const CONSOLE_OFF = {
  debug: false,
  error: false,
  info: false,
  metric: false,
  trace: false,
  warn: false
};

const USER_RECORDTYPE = RecordType.USER.toLowerCase();
const LOGGER_NAME = 'exc.metrics-runtime.AdobeMetricsRuntime';
const metrics = new Metrics(LOGGER_NAME);

/**
 * A private helper function to populate a History metric. Move to gateway.
 *
 * @param {Metric} metric The metric object emitting the history event.
 */
function makeHistoryPushOrReplace(metric: Metric): void {
  if (!metric.data) {return;}

  // find the history object - either the data object or the first element
  let arg;
  if (Array.isArray(metric.data)) {
    arg = metric.data[0];
  } else {
    arg = metric.data;
    delete metric.data;
  }

  // normalize the history object
  let path = arg.path;
  const state = arg.state;
  if (path) {
    if (path === Object(path)) {
      if (state) {
        path.state = state;
      }
    } else {
      path = {
        state,
        url: path
      };
    }
  }

  // put the history object back into data in the correct place
  if (Array.isArray(metric.data)) {
    metric.data[0] = path;
  } else {
    metric.data = path;
  }
}

/**
 * Validate and set the runtime environment to 'dev' | 'stage' | 'prod' based
 * on an input enironment which will be case insensitive string-matched. If
 * env is not stage | prod, then it defaults to dev.
 *
 * @param {string} env The string representing the environment.
 */
function setEnvironment(env?: string): void {
  if (!env) {return;}
  env = env && env.toLowerCase();
  switch (env) {
    case 'prod':
    case 'stage':
      break;
    default:
      env = 'dev';
  }
  (global as any).adobeMetrics.runtime.environment = env;
}

/**
 * Validate and set the runtime mode to
 * 'normal' | 'verbose' | 'quiet' | 'test' | 'off'.
 *
 * @param {string} mode The mode of metrics operation, default to 'normal'.
 */
function setMode(mode: string): void {
  mode = mode && mode.toLowerCase();

  // make sure mode is one of the supported values
  switch (mode) {
    case 'verbose':
    case 'vvv':
    case 'quiet':
    case 'test':
    case 'off':
      break;
    default:
      mode = 'normal';
  };

  const adobeMetrics = (global as any).adobeMetrics;
  const runtime = adobeMetrics.runtime;
  runtime.mode = mode;
  switch (mode) {
    case 'verbose':
      Object.assign(runtime.options, {
        console: CONSOLE_VERBOSE,
        timestamp: true
      });
      Object.assign(runtime.options.transport, {
        flushPeriod: 1000,
        transmit: true
      });
      break;
    case 'vvv':
      Object.assign(runtime.options, {
        console: CONSOLE_VVV,
        timestamp: true
      });
      Object.assign(runtime.options.transport, {
        flushPeriod: 1000,
        transmit: true
      });
      break;
    case 'quiet':
      Object.assign(runtime.options, {
        console: CONSOLE_OFF,
        timestamp: false
      });
      Object.assign(runtime.options.transport, {
        flushPeriod: PROD_FLUSH_PERIOD,
        transmit: true
      });
      break;
    case 'test':
      Object.assign(runtime.options, {
        console: CONSOLE_VERBOSE,
        timestamp: true
      });
      Object.assign(runtime.options.transport, {
        flushPeriod: 0,
        transmit: false
      });
      break;
    case 'off':
      Object.assign(runtime.options, {
        console: CONSOLE_OFF,
        timestamp: false
      });
      Object.assign(runtime.options.transport, {
        flushPeriod: 0,
        transmit: false
      });
      break;
    default:
      Object.assign(runtime.options, {
        console: CONSOLE_NORMAL,
        timestamp: false
      });
      Object.assign(runtime.options.transport, {
        flushPeriod: PROD_FLUSH_PERIOD,
        transmit: true
      });
      break;
  }
}

/**
 * Make make sure the metricsState is non-null and has the current value of
 * adobeMetrics pageState.
 *
 * @param {MetricsState} state The metricsState to be annotated.
 * @returns {MetricsState} An updated state, or a new state if the state param was null.
 */
function applyPageState(state?: MetricsState): MetricsState {
  return Object.assign({}, state, {
    pageState: Object.assign({}, (global as any).adobeMetrics.pageState)
  });
}

/**
 * Compare user with current Metrics.user properties. Only userId, groupId and
 * sessionId are used to perform the compare. Other properties are considered
 * state properties, but differences in those properties do not designate a
 * different user.
 *
 * @param {User} user The user properties to check against the
 * current Metrics.user.
 * @returns {boolean} True if the provided user is the current metrics user.
 */
function isCurrentUser(user: User): boolean {
  const previousUser = (global as any).adobeMetrics.user;
  if (user && previousUser) {
    return user.userId === previousUser.userId && user.groupId === previousUser.groupId
      && user.sessionId === previousUser.sessionId && previousUser.event === user.event;
  }
  return false;
}

function _setUser(user: User, state: MetricsState): void {
  // if event or data are set on the incoming user object, force an update
  const updated = user.data || user.event;

  const adobeMetrics = (global as any).adobeMetrics;
  user = Object.assign({}, adobeMetrics.user, user, {
    dateNow: Date.now(),
    event: user.event,
    recordType: 'User'
  });

  if (Transport.transmit && (updated || !isCurrentUser(user))) {
    if ((global as any).document && (global as any).document.cookie && (global as any).document.cookie.search(re) > -1
       && user.metricsState) {
      user.metricsState.flags = Object.assign({}, user.metricsState.flags, {user: {optOut: true}});
    }
    Payload.addUser(Object.assign({}, user), state);
    // log that setUser was called with info on which attributes were set
    // do not add displayName, userId or any other actual PII to this
    const m: Metric = {
      data: {
        accountType: user.accountType !== undefined,
        attributes: user.attributes !== undefined,
        authOrigin: user.authOrigin !== undefined,
        authSystem: user.authSystem !== undefined,
        data: user.data !== undefined,
        displayName: user.displayName !== undefined,
        event: user.event !== undefined,
        groupId: user.groupId !== undefined,
        groupName: user.groupName !== undefined,
        ipAddress: user.ipAddress !== undefined,
        language: user.language !== undefined,
        privileges: user.privileges !== undefined,
        sessionId: user.sessionId !== undefined,
        userAgent: user.userAgent !== undefined,
        userId: user.userId !== undefined
      },
      dateNow: user.dateNow,
      event: 'Metrics.setUser',
      level: Level.INFO,
      name: LOGGER_NAME,
      recordType: RecordType.EVENT,
      user: {
        groupId: user.groupId,
        sessionId: user.sessionId,
        userId: user.userId
      }
    };
    Payload.addMetric(m, applyPageState(state));
  }
  adobeMetrics.user = user;

  // Set user in analytics using metric user
  const METRIC_USER: UserData = {
    accountType: user.accountType || ANONYMOUS_CONSTANT,
    attributes: user.attributes,
    authOrigin: user.authOrigin,
    authSystem: user.authSystem || ANONYMOUS_CONSTANT,
    corpId: user.groupId || ANONYMOUS_CONSTANT,
    corpName: user.groupName || ANONYMOUS_CONSTANT,
    id: user.userId || ANONYMOUS_CONSTANT,
    language: user.language || ANONYMOUS_CONSTANT,
    privileges: user.privileges
  };
  // It will send the current user to both Aanalytics and telemetry
  metrics.analytics.trackUser(METRIC_USER);
}

/**
 * Record current User Properties for including in Metrics records.
 * Only add new user to Payload user array but always set Metrics.user.
 * Supplied properties (except for event) will be merged into the existing
 * user object, which allows for updating some properties such as the active
 * group, without losing other state previously provided. The event property
 * from the user parameter will be recorded as-is, including if undefined.
 *
 * @param {User} user The user properties to record.
 * @param {string} version The Metrics sdk version that created this user.
 */
function setUser(user: User, version?: string): void {
  _setUser(user, {sdkVersion: version || BUILD_VERSION} as any);
}

function _clearUser(state: MetricsState): void {
  const props = {
    dateNow: Date.now(),
    event: undefined,
    recordType: 'User'
  };
  const user = Object.assign({}, props);
  if (Transport.transmit && !isCurrentUser(user)) {
    Payload.addUser(Object.assign({}, user), state);

    // log that clearUser was called
    // do not add displayName, userId or any other actual PII to this
    const m: Metric = {
      dateNow: user.dateNow,
      event: 'Metrics.clearUser',
      level: Level.INFO,
      name: LOGGER_NAME,
      recordType: RecordType.EVENT,
      user: {}
    };
    Payload.addMetric(m, applyPageState(state));
  }
  (global as any).adobeMetrics.user = user;

  // Clear user in analytics using anonymous values for required fields
  const ANONYMOUS_METRIC_USER: UserData = {
    accountType: ANONYMOUS_CONSTANT,
    authSystem: ANONYMOUS_CONSTANT,
    corpId: ANONYMOUS_CONSTANT,
    corpName: ANONYMOUS_CONSTANT,
    id: ANONYMOUS_CONSTANT,
    language: ANONYMOUS_CONSTANT
  };
  // It will send the anonymous user to OMEGA and an Analytics metric to telemetry
  metrics.analytics.trackUser(ANONYMOUS_METRIC_USER);
}

/**
 * Clear User profile and group, typically called upon logout or a detected
 * timeout. This method will reset Metrics.user back to an anonymous user.
 *
 * @param {string} version The Metrics sdk version that cleared this user.
 */
function clearUser(version?: string): void {
  _clearUser({sdkVersion: version || BUILD_VERSION} as any);
}

/**
 * Special processing for Analytics record types.
 *
 * @param {Metric} metric The metric being processed.
 */
function logAnalytics(metric: Metric): void {
  // return quickly for things we don't handle
  if (!metric.event || Array.isArray(metric.event)) {return;}

  const omegaEvents = ['event', 'page', 'user'];
  const telemetryEvents = ['Event', 'Page', 'User'];
  const prefix = 'Analytics.';
  const g = global as any;
  const d: DigitalData = g.digitalData = g.digitalData || {event: [], page: {solution: {}}, user: {}};
  let e: EventData | PageData | UserData;
  const dataIsArray = Array.isArray(metric.data);

  if (omegaEvents.includes(metric.event)) {
    if (dataIsArray) {
      metric.data = [d, ...metric.data];
    } else if (metric.data) {
      metric.data = [d, metric.data];
    } else {
      metric.data = d;
    }
    metric.event = prefix + metric.event;
  } else if (telemetryEvents.includes(metric.event)) {
    // metric event contains a EventData, PageData or UserData
    // put it into window.DigitalData
    e = dataIsArray ? metric.data[0] : metric.data;

    switch (metric.event) {
      case 'Event':
      default:
        d.event = d.event || [];
        e && d.event.push(e as EventData);
        break;
      case 'Page':
        d.page = d.page || {solution: {}} as PageData;
        d.page = Object.assign({}, d.page, e);
        d.page.url = g.location && g.location.href;
        d.page.name = d.page.name || document.title;
        d.page.hierarchy = d.page.hierarchy || d.page.name;
        break;
      case 'User':
        d.user = Object.assign({}, d.user, e);
        break;
    }

    g._satellite && g._satellite.track && g._satellite.track(metric.event.toLowerCase());
    if (dataIsArray) {
      metric.data[0] = Object.assign({}, d);
    } else {
      metric.data = Object.assign({}, d);
    }
    metric.event = prefix + 'track' + metric.event;
  }
}

/**
 * Special processing for History record types. Moves to gateway.
 *
 * @param {Metric} metric The metric being processed.
 */
function logHistory(metric: Metric): void {
  switch (metric.event) {
    case 'back':
      metric.counter = -1;
      break;
    case 'forward':
      metric.counter = 1;
      break;
    case 'go':
      metric.counter = metric.data && metric.data[0].n;
      break;
    case 'push':
    default:
      makeHistoryPushOrReplace(metric);
      break;
    case 'replace':
      makeHistoryPushOrReplace(metric);
      break;
  }
  metric.event = 'History.' + metric.event;
}

/**
 * Special processing for Location record types. Moves
 * to gateway.
 *
 * @param {Metric} metric The metric being processed.
 */
function logLocation(metric: Metric): void {
  metric.event = 'Location.' + metric.event;
}

/**
 * Record a metric object to remote storage.
 *
 * @param {Metric} m The metric to be recorded.
 * @returns {string | undefined} A boolean indicating whether processed
 * merics should be flushed.
 */
function processMetric(m: Metric): string | undefined {
  let flushReason = undefined;

  // must have a level for local console statement
  m.level = m.level || Level.INFO;

  const a = (global as any).adobeMetrics;
  if (!m.user && a.user) {
    m.user = {
      groupId: a.user.groupId,
      sessionId: a.user.sessionId,
      userId: a.user.userId
    };
  }

  a.pageState.handleChange(m);

  switch (m.recordType) {
    case RecordType.ANALYTICS:
      logAnalytics(m);
      break;
    case RecordType.HISTORY:
      logHistory(m);
      break;
    case RecordType.LOCATION:
      logLocation(m);
      break;
    default:
      break;
  }

  const key = m.level.toLowerCase();
  const runtime = (global as any).adobeMetrics.runtime;
  if ((runtime.options.console as any)[key]) {
    (console as any)[key](`${m.name || ''}:${m.event || ''}: ${m.message || ''}`,
      runtime.options.console.metric ? m : m.data || '');
  }

  m.metricsState = applyPageState(m.metricsState);

  if (Transport.transmit) {
    // for now, event arrays are processed client-side. Move to gateway.
    const originalEvent = m.event;
    if (Array.isArray(originalEvent) && originalEvent.length > 0) {
      originalEvent.forEach(e => {
        m.event = e;
        Payload.addMetric(m, Object.assign({}, m.metricsState,
          {guid: a.guid, sdkVersion: m.metricsState && m.metricsState.sdkVersion || METRICS_VERSION}));
      });
    } else {
      Payload.addMetric(m, Object.assign({}, m.metricsState,
        {guid: a.guid, sdkVersion: m.metricsState && m.metricsState.sdkVersion || METRICS_VERSION}));
    }
    m.event = originalEvent;
    flushReason = flushReason || isFlushEvent(m);
  }
  return flushReason;
}

async function processQueue(flush: string | undefined = undefined): Promise<number> {
  const now = Date.now();
  const a = (global as any).adobeMetrics;
  let m;
  let count = 0;
  while ((m = a.queue.shift()) !== undefined) {
    m.dateNow = m.dateNow || now;
    const reason = processMetric(m);
    flush = flush || reason;
    ++count;
  }

  if (Transport.transmit) {
    return Transport.send(flush);
  }
  return Promise.resolve(count);
}

/**
 * Determine if a metric is an event that should trigger a flush to remote storage.
 *
 * @param {Metric} metric The metric properties to be checked.
 * @returns {string | undefined} A string reason if queued metrics should be flushed.
 */
function isFlushEventFn(metric: Metric): string | undefined {
  // Location records always flush
  if (RecordType.LOCATION === metric.recordType) {
    return 'location';
  }

  // Event records flush if PAGE_LOAD_DONE
  if (metric.event) {
    let done = false;
    const events = Array.isArray(metric.event) ? metric.event : [metric.event];
    done = events.some((e: string): boolean => {
      return MetricsEvents.PAGE_LOAD_DONE === e;
    });
    return done ? 'pageDone' : undefined;
  }
  return undefined;
}
let isFlushEvent = isFlushEventFn;

/**
 * Flush all metric to remote storage.
 *
 * @returns {Promise} A Promise of the number of metrics flushed.
 */
async function flush(): Promise<number> {
  return await processQueue('flushAPI');
}

/**
 * Record a metric object to remote storage.
 *
 * @param {Record} r The metric to be recorded.
 * @returns {Promise} A Promise of the number of metrics sent (if any).
 * Note the return value may be undefined, and should be ignored by the caller.
 */
async function write(r: Record): Promise<number> {
  r.dateNow = r.dateNow || Date.now();

  if (!r.recordType) {
    return Promise.reject(new TypeError('RecordType must be defined'));
  }

  // check if we need to do special handling
  const recordType = r.recordType.toLowerCase();
  if (USER_RECORDTYPE === recordType) {
    return Promise.reject(new TypeError('The User RecordType cannot be store()\'d. Read the jsdocs for the User RecordType.'));
  }

  r.metricsState = applyPageState(r.metricsState);
  (global as any).adobeMetrics.queue.push(r);

  // TODO: optimize immediate processing
  return processQueue();
}

/**
 * Log a message to remote storage, with an optional data payload. In
 * developer mode, the log message will also be output to the console.
 * This method emulates console.log().
 *
 * @param {RecordType} recordType The type of metric being logged, defaults
 * to LOG.
 * @param {string} level The log level, which can be overridden by a
 * level object in the args list in the form {level: Level.DEBUG}.
 * @param {string} name The logger name.
 * @param {string} message The log message.
 * @param {string} event An event string or array of strings, typically
 * these are application-specific enums.
 * @param {any} args The rest of the objects to be logged. Primitives will
 *  be appended as strings to the message, and objects will be stored in an
 *  array in the data bag on the metric.
 */
function log(recordType: RecordType, level: Level, name?: string, message?: string,
  event?: string | string[], ...args: any): void {
  const m: Metric = {
    data: [...args],
    event,
    level,
    message,
    name,
    recordType
  };
  ((global as any).adobeMetrics as AdobeMetrics).write(m);
}

function time(this: Timer, event?: string | {event: string}, ...args: any): number {
  this.dateNow = Date.now(); //record timestamp first
  (this as any).now = performance.now();

  // clear old state
  delete this.data;
  delete this.event;
  delete this.message;
  this.level = Level.INFO;

  // calculate new event
  if (event && typeof event === 'object') {
    this.event = event.event;
  } else {
    this.event = (this.prefix || '') + (this.prefix && event ? '.' : '') + (event || '');
  }
  this.data = [...args];
  this.epoch = this.epoch || this.now;
  this.duration = this.now - this.epoch;
  ((global as any).adobeMetrics as AdobeMetrics).write(Object.assign({}, this));
  return this.duration;
}

/**
 * Construct a property bag metric of a specific type using the properties
 * supplied, if any. The returned object can be Object assign()'d to the
 * constructed metric to allow for function definitions in a subclass.
 * The Metric level will default to INFO. To specify a different level, add
 * a level object to the args paramaters in the form
 * {level: Level.DEBUG}.
 *
 * @param {RecordType} recordType The enum for which metric type to create.
 * @param {string} name The name gives context of what is being measured.
 * @param {any} args Optional objects with properties to be applied to the
 * recorded metrics.
 * @returns {Metric} An object conforming to the Metric interface that
 * implements Timer.
 */
function create(recordType: RecordType, name?: string, ...args: any): Metric {
  let metric;
  const context = args[0];
  const prefix = args[1];
  const props = args.slice(2);;

  const dateNow = Date.now();
  const epoch = performance.now();
  switch (recordType) {
    case RecordType.TIMER:
    default:
      metric = Object.assign({}, {
        dateNow,
        epoch,
        level: Level.INFO,
        metricsState: {
          context,
          correlationId: uuidv1(),
          sdkVersion: METRICS_VERSION
        },
        name,
        now: epoch,
        prefix,
        recordType: RecordType.TIMER,
        time
      });
      metric.time.bind(metric);
      metric.time('start', ...props);
      break;
  }
  return metric;
}

/**
 * Set the application context information into the environment,
 * so that it can be recordedmwith each metric.
 *
 * @param {Application} app The application information,
 * conforming to the Metric.application property bag. Solution
 * or sub-application can be merged in by passing just that portion
 * of the object, such as {solution: {id: 'solution2'}}.
 *
 * To delete application information, pass undefined.
 */
function setApplication(app: Application): void {
  const a = (global as any).adobeMetrics;

  if (app) {
    if (app.id && a.application && a.application.id === app.id) {
      Object.assign(a.application, app);
    } else if (app && !app.id && app.solution) {
      a.application = Object.assign({}, a.application, app);
    } else {
      a.application = Object.assign({}, app);
    }
  } else {
    delete a.application;
  }

  metrics.event('Metrics.setApplication', app);
}

/**
 * Configure the Metrics package. These options control the
 * operatonal behavior.
 *
 * If mode is omitted, it will default to "verbose" for dev
 * environment, and "normal" for stage and prod.
 *
 * The current configuration will be updated as compliant as possible
 * according to the supplied configuration object, and returned. If
 * no configuration object is provided, then the current
 * configuration is validated and returned.
 *
 * @param {Configuration} config An optional congfiguration
 * object for the desired state.
 * @returns {Configuration} The current and validated configuration.
 */
function configure(config?: Configuration): Configuration {
  const a = (global as any).adobeMetrics;
  const runtime = a.runtime;

  config && setEnvironment(config.environment);
  // derive mode from environment if mode is not specified and hasn't
  // previously been set
  if (config && !config.mode && !runtime.mode) {
    switch (runtime.environment) {
      case 'stage':
      case 'prod':
        setMode('normal');
        break;
      default:
        setMode('verbose');
        break;
    }
  }
  if (config && config.mode) {
    setMode(config.mode);
  }
  if (config && config.mode === 'off') {
    config.batch = false;
  }
  if (config && config.batch !== undefined && a.runtime.options.transport.batch !== config.batch) {
    runtime.options.transport.batch = config.batch;
    if (config.batch) {
      isFlushEvent = isFlushEventFn;
      a.init.processQueueInterval = setInterval(processQueue, 1000);
    } else {
      isFlushEvent = (): string => {return 'immediate';};
      clearInterval(a.init.processQueueInterval);
      delete a.init.processQueueInterval;
    }
  }
  (runtime.options.transport as any).maxPayloadSize = config && config.maxPayloadSize || (runtime.options.transport as any).maxPayloadSize;

  config && config.user && a.setUser(config.user, runtime.version);
  a.deviceId = config && config.deviceId || a.deviceId;
  a.instanceId = config && config.instanceId || a.instanceId;
  Transport.init();

  try {
    if (config) {
      if (config.instanceId) {
        (global as any).sessionStorage.setItem('adobeMetrics.instanceId', config.instanceId);
      }
      if (config.deviceId) {
        (global as any).localStorage.setItem('adobeMetrics.deviceId', config.deviceId);
      }
    }
  } catch (e) {
    // if sessionStorage isn't supported, device and instance tracking won't
    // be possible the environment is probably nodejs, which does not have
    // local or session storage
  }

  const c = {
    batch: a.runtime.options.transport.batch,
    deviceId: a.deviceId,
    environment: a.runtime.environment,
    instanceId: a.instanceId,
    mode: a.runtime.mode,
    user: Object.assign({}, a.user)
  };
  // remove user.event and dateNow so that they are not re-recorded
  delete c.user.event;
  delete c.user.dateNow;
  return c;
}

/**
 * Use Parametrized Queries with deep-linking to ADX.
 *
 * @param {string} query The query to execute in ADX.
 * @returns {string} Deep link to data explore.
 */
function getQueryLink(query: string): string | undefined {
  const env = (global as any).adobeMetrics.runtime.environment;
  const encodedQueryString = encodeURI(query);
  const queryURL = `https://dataexplorer.azure.com/clusters/${env}metrics.eastus2/databases/Metrics?query=${encodedQueryString}`;
  return queryURL;
}

/**
 * An interface that describes the global adobeMetrics object.
 *
 * @typedef AdobeMetricsRuntime
 */
export default interface AdobeMetricsRuntime extends AdobeMetrics {
  application?: Application;
  configure(config?: Configuration): Configuration;
  deviceId: string;
  guid?: string;
  instanceId: string;
  pageState?: PageState;
  readonly runtime: Runtime;
  readonly user?: User;
  windowId: string;

  // deprecated
  clearUser(version?: string): void;
  setApplication(app: Application): void;
  setEnvironment(env: string): void;
  setMode(mode: string): void;
  setUser(user: User, version?: string): void;
}

/**
 * Initialize the global.adobeMetrics object. This function
 * is agnostic wrt browser vs nodejs - in either environmnet
 * the basic adobeMetrics object will be initialized independent.
 *
 * @returns {AdobeMetricsRuntime} A pointer to the global
 *  adobeMetrics object that has been properly initialized.
 */
export function initialize(): AdobeMetricsRuntime {
  const g = global as any;
  const a = g.adobeMetrics = g.adobeMetrics || {};
  const now = Date.now();
  a.currentHRef = a.currentHRef || '';
  a.init = a.init || {};
  a.options = a.options || {
    console: {},
    transport: {
      batch: true,
      flushPeriod: PROD_FLUSH_PERIOD,
      maxPayloadSize: 500000
    }
  };
  a.pageState = a.pageState || new PageState();
  a.queue = a.queue || [];
  a.sdkVersion = a.sdkVersion || METRICS_VERSION;
  a.timeOrigin = a.timeOrigin || performance.timeOrigin || performance.timing && performance.timing.navigationStart || now;
  a.user = a.user || {
    dateNow: now,
    event: 'Initialization',
    recordType: RecordType.USER
  };
  a.windowId = a.windowId || uuidv1();

  // global functions
  a.create = create;
  a.flush = flush;
  a.log = log;
  a.write = write;

  // deprecated functions
  a.configure = configure;
  a.setApplication = setApplication;
  a.setEnvironment = setEnvironment;
  a.clearUser = clearUser;
  a.setUser = setUser;

  // runtime
  const runtime = a.runtime = a.runtime || {};
  runtime.environment = runtime.environment || 'dev';
  runtime.mode = runtime.mode || 'normal';
  runtime.options = runtime.options || {};
  runtime.options.console = runtime.options.console || {
    debug: false,
    error: true,
    info: false,
    metric: false,
    trace: false,
    warn: true
  };
  runtime.options.timestamp = runtime.options.timestamp || false;
  runtime.options.transport = runtime.options.transport || {
    flushPeriod: PROD_FLUSH_PERIOD,
    maxPayloadSize: 500000,
    transmit: true
  };
  runtime.version = BUILD_VERSION;

  try {
    a.instanceId = g.sessionStorage.getItem('adobeMetrics.instanceId');
    if (!a.instanceId) {
      a.instanceId = uuidv1();
      g.sessionStorage.setItem('adobeMetrics.instanceId', g.adobeMetrics.instanceId);
    }

    a.deviceId = g.localStorage.getItem('adobeMetrics.deviceId');
    if (!a.deviceId) {
      a.deviceId = uuidv1();
      g.localStorage.setItem('adobeMetrics.deviceId', g.adobeMetrics.deviceId);
    }
  } catch (e) {
    // if sessionStorage isn't supported, device and instance tracking
    // won't be possible
    a.deviceId = a.instanceId = a.windowId;
  }
  // add adx deep link for client to access ADX without copy and paste
  const queryGetByInstanceId = `Log | where InstanceId == "${a.instanceId}" | order by TimeStamp asc`;
  a.queryURL = getQueryLink(queryGetByInstanceId);

  configure({batch: a.runtime.options.transport.batch || true, environment: a.runtime.environment, mode: a.runtime.mode});
  return a;
}
